import React, { useEffect, useRef, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import {
  toEnd,
  toStart,
} from "@/components/molecules/insight/PenPortraitView/utils";
import { InsightResult } from "@/types";
import { toBubbleColor, toPercentage, toIndex } from "@/utils";

interface DemographicCardProps {
  demographic: string;
  values: InsightResult[];
  score: string;
  center?: boolean;
  gradient?: string[];
}

const DemographicCard: React.FC<DemographicCardProps> = ({
  demographic,
  values,
  score,
  center,
  gradient
}) => {
  const theme = useTheme();

  return (
    <Stack spacing={1}>
      <Typography variant="body1" sx={{ mb: 1 }}>{demographic}</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          {toStart(demographic)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {toEnd(demographic)}
        </Typography>
      </Box>
      <Box
        display="flex"
        px={1}
        gap={0.5}
        width="100%"
        sx={{
          [theme.breakpoints.down('lg')]: {
            height: 48,
          },
          [theme.breakpoints.up('lg')]: {
            height: 25,
            transition: theme.transitions.create(['height', 'padding'], {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeInOut,
            }),
          },
          '.category-item:hover &': {
            [theme.breakpoints.up('lg')]: {
              height: 48,
            }
          }
      }}
      >
        {values.map((row, idx) => (
          <HelpBox
            key={idx}
            title={`${row.tag_name} (${
              score === "rate"
                ? toPercentage(row[score as keyof InsightResult] as number)
                : toIndex(row[score as keyof InsightResult] as number)
            })`}
          >
            <Box
              sx={{
                display: "flex",
                flex: 1,
                minWidth: 0,
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
                borderRadius: "4px",
                backgroundColor: toBubbleColor(
                  row[score as keyof InsightResult] as number,
                  score,
                  gradient ?? theme.palette.gradient
                ),
                transition: 'background-color 0.2s',
                '&:hover': {
                  filter: 'brightness(1.15)',
                },
              }}
            >
              <Box
                className="demog-text"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  opacity: 0,
                  transform: 'translateY(4px)',
                  transition: theme.transitions.create(['opacity', 'transform'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,           // 🎯 Clamps to max 2 lines
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'normal',         // Allows wrapping
                    fontSize: demographic === 'Lifestage' ? '10px' : '12px'
                  }}
                >
                  {row.tag_name}
                </Typography>
              </Box>
            </Box>
          </HelpBox>
        ))}
      </Box>
    </Stack>
  );
};

export default DemographicCard;
